import { useCallback } from 'react';

import { createActionsHook, createHook, createStore } from 'react-sweet-state';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import type {
	ChatContextPayload,
	ChatContextState,
} from '@atlaskit/rovo-triggers/chat-context/types';
import type {
	BrowserContextPayloadData,
	DashboardInsightsActionsPayloadData,
	DatabaseContextPayloadData,
	EditorContextPayloadData,
	JiraCreateContextPayloadData,
	WhiteboardContextPayloadData,
	WorkflowContextPayloadData,
} from '@atlaskit/rovo-triggers/types';
import type { BrowserContextCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/browser-context-command';
import type { JiraContextCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/jira-draft-data-command';
import type { ReadContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';
import type { ReadSpaceCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/read-space-command';

export type Store = {
	editorContext: EditorContextPayloadData;
	whiteboardContext?: WhiteboardContextPayloadData;
	databaseContext?: DatabaseContextPayloadData;
	browserContext?: BrowserContextPayloadData;
	dashboardContext?: DashboardInsightsActionsPayloadData;
	jiraWorkflowContext?: WorkflowContextPayloadData;
	jiraCreateContext?: JiraCreateContextPayloadData;
	// All the new context will be added under this key
	aiFeatureContext: ChatContextState;
	/**
	 * For AI-first creation, we need to store the content targets in the store.
	 * This is used to determine which content target to use for content mutations.
	 * When aifc_multiple_context gate is enabled, this supports arrays of targets per context.
	 * Otherwise, it stores single targets for backward compatibility.
	 */
	contentTargets: Partial<Record<ContentContext, ContentTarget | ContentTarget[]>>;
	/**
	 * The id of the current content target, which will be used to determine which context
	 * to use for create and edit mutations.
	 */
	currentTargetId: string | number | undefined;
};

/**
 * Content types supported in the AI-first creation flow.
 */
export type ContentType = 'page' | 'whiteboard' | 'database';

/**
 * A piece of content which Rovo chat is able to communicate with directly in order
 * to mutate it after it has been created. Considerations:
 *
 * - Rovo <-> content can communicate via inline experience inside of the content (e.g. the inline toolbar)
 * - Rovo <-> content[] is a possibility - consider 'layering' of the staging area in AI-first creation.
 * - Rovo <-> content[] is a future possibility with more and more content mutations being possible via Rovo.
 */
type ContentTarget = {
	/**
	 * The id of the content target, which will be supplied to backend APIs,
	 * and yielded in every chunk of network stream events.
	 */
	id: string | number;
	/**
	 * The type of the content target, which will be used to determine which context
	 * to use for create and edit mutations.
	 */
	type?: 'page' | 'whiteboard' | 'space' | 'jira' | 'database';
	/**
	 * Whether the content target can provide context for create and edit mutations.
	 */
	canProvideContext: boolean;
};

/**
 * Content is rendered in a Rovo context. If it is 'real' content outside of the staging area, it is rendered
 * in the `global` Rovo context (with Rovo side-chat being a global experience). If it is rendered in the staging
 * area modal, then it is rendered to the left, with chat embedded in the same UI experience.
 */
export type ContentContext = 'staging-area' | 'global';

const store = createStore({
	initialState: {
		editorContext: undefined,
		whiteboardContext: undefined,
		databaseContext: undefined,
		browserContext: undefined,
		dashboardContext: undefined,
		jiraWorkflowContext: undefined,
		aiFeatureContext: {},
		contentTargets: {},
		currentTargetId: undefined,
	} as Store,
	actions: {
		getAllContext:
			() =>
			({ getState }) =>
				getState(),
		getAIFeatureContext:
			() =>
			({ getState }) => {
				const currentState = getState().aiFeatureContext;
				// Delete all properties that are undefined
				Object.keys(currentState).forEach(
					(key) => currentState[key] === undefined && delete currentState[key],
				);
				// Avoid sending an empty object
				return Object.keys(currentState).length ? currentState : undefined;
			},
		setAIFeatureContext:
			(chatContextData: ChatContextPayload) =>
			({ getState, setState }) => {
				const currentAIFeatureContextState = getState().aiFeatureContext;
				const { contextKey, setContext } = chatContextData;
				if (contextKey) {
					const contextUpdated = setContext(currentAIFeatureContextState[contextKey]);

					setState({
						aiFeatureContext: {
							...currentAIFeatureContextState,
							[contextKey]: contextUpdated,
						},
					});
				}
			},
		getDashboardContext:
			() =>
			({ getState }) =>
				getState().dashboardContext,
		setDashboardContext:
			(dashboardContext: DashboardInsightsActionsPayloadData) =>
			({ setState }) => {
				setState({
					dashboardContext,
				});
			},
		getJiraWorkflowContext:
			() =>
			({ getState }) =>
				getState().jiraWorkflowContext,
		setJiraWorkflowContext:
			(jiraWorkflowContext: WorkflowContextPayloadData) =>
			({ setState }) => {
				setState({
					jiraWorkflowContext,
				});
			},
		getJiraCreateContext:
			() =>
			({ getState }) =>
				getState().jiraCreateContext,
		setJiraCreateContext:
			(jiraCreateContext: any) =>
			({ setState }) => {
				setState({
					jiraCreateContext,
				});
			},
		getEditorContext:
			() =>
			({ getState }) =>
				getState().editorContext,
		setEditorContext:
			(editorContext: EditorContextPayloadData) =>
			({ setState }) => {
				setState({
					editorContext,
				});
			},
		getWhiteboardContext:
			() =>
			({ getState }) =>
				getState().whiteboardContext,
		setWhiteboardContext:
			(whiteboardContext: WhiteboardContextPayloadData) =>
			({ setState }) => {
				setState({
					whiteboardContext,
				});
			},
		getDatabaseContext:
			() =>
			({ getState }) =>
				getState().databaseContext,
		setDatabaseContext:
			(databaseContext: DatabaseContextPayloadData) =>
			({ setState }) => {
				setState({
					databaseContext,
				});
			},
		mergeDatabaseContext:
			(partial: Partial<NonNullable<DatabaseContextPayloadData>>) =>
			({ getState, setState }) => {
				const current = getState().databaseContext;
				setState({
					databaseContext: current
						? { ...current, ...partial }
						: (partial as DatabaseContextPayloadData),
				});
			},
		getBrowserContext:
			() =>
			({ getState }) =>
				getState().browserContext,
		setBrowserContext:
			(browserContext: BrowserContextPayloadData) =>
			({ setState }) => {
				setState({
					browserContext,
				});
			},
		getTarget:
			(contentContext: ContentContext, activeTargetId?: string | number) =>
			({ getState }) => {
				const targets = getState().contentTargets[contentContext];

				if (!targets) {
					return undefined;
				}

				if (fg('aifc_multiple_context')) {
					// New mode: targets is always an array
					const targetList = targets as ContentTarget[];

					// If activeTargetId provided, find it; otherwise return first target
					if (activeTargetId) {
						return targetList.find((t) => t.id === activeTargetId);
					}
					return targetList[0];
				}

				// Legacy mode: single target
				return targets as ContentTarget | undefined;
			},
		getContentContext:
			(contentId: string | number) =>
			({ getState }) => {
				const entries = Object.entries(getState().contentTargets);
				for (const [context, targets] of entries) {
					const targetList = fg('aifc_multiple_context')
						? (targets as ContentTarget[])
						: [targets as ContentTarget];

					if (targetList.some((t) => t.id === contentId)) {
						return context as ContentContext;
					}
				}
				return undefined;
			},
		getActiveContext:
			() =>
			({ getState }): ContentContext => {
				const entries = getState().contentTargets;
				// The rules here determine the 'rank' in which Rovo layers are resolved into "which layer is currently active".
				// They map to intended UX - in AIFC, the staging area is a modal-powered experience, on top of global experiences.
				// Consider the scenario of sending a message - the user will be interacting with the topmost layer.
				if (entries['staging-area']) {
					return 'staging-area';
				}
				return 'global';
			},
		hasTarget:
			(contentContext: ContentContext) =>
			({ getState }) =>
				!!getState().contentTargets[contentContext],
		setTarget:
			(toAdd: ContentContext, target: ContentTarget) =>
			({ getState, setState }) => {
				const currentTargets = getState().contentTargets;

				if (fg('aifc_multiple_context')) {
					// New mode: store as array, append if not already present
					const existing = currentTargets[toAdd] as ContentTarget[] | undefined;
					const targetList = existing ?? [];

					// Avoid duplicates by checking if target with same id exists
					// & filter out any targets with empty IDs to avoid stale targets
					const filtered = targetList.filter((t) => t.id !== '' && t.id !== target.id);
					filtered.push(target);

					setState({
						contentTargets: { ...currentTargets, [toAdd]: filtered },
					});
				} else {
					// Legacy behavior: single target
					setState({
						contentTargets: { ...currentTargets, [toAdd]: target },
					});
				}
			},
		clearTarget:
			(toRemove: ContentContext, targetId?: string | number) =>
			({ getState, setState }) => {
				const currentState = getState().contentTargets;
				const nextState: Partial<Record<ContentContext, ContentTarget | ContentTarget[]>> = {};

				for (const key of Object.keys(currentState)) {
					const k = key as ContentContext;
					if (k !== toRemove) {
						nextState[k] = currentState[k];
					} else if (targetId && fg('aifc_multiple_context')) {
						// If multiple-context mode with a specific targetId, filter out that target
						const targetList = currentState[k] as ContentTarget[];
						const filtered = targetList.filter((t) => t.id !== targetId);
						if (filtered.length > 0) {
							nextState[k] = filtered;
						}
					}
				}

				setState({ contentTargets: nextState });
			},
		setCurrentTargetId:
			(targetId: string | number) =>
			({ setState }): void => {
				setState({
					currentTargetId: targetId,
				});
			},
		getCurrentTargetId:
			() =>
			({ getState }): string | number | undefined =>
				getState().currentTargetId,
	},
});

export const useChatContextStoreActions = createActionsHook(store);
export const useChatContextEditorContext = createHook(store, {
	selector: (state) => state.editorContext,
});
export const useChatContextWhiteboardContext = createHook(store, {
	selector: (state) => state.whiteboardContext,
});
export const useChatContextDatabaseContext = createHook(store, {
	selector: (state) => state.databaseContext,
});
export const useChatContextBrowserContext = createHook(store, {
	selector: (state) => state.browserContext,
});

// For testing
export { store };

export const useChatContextProvider = ({
	requestBrowserContent,
	getContentBody,
	getStagingSpace,
	useCurrentNonEditingPageAsContext,
	requestJiraContext,
}: {
	requestBrowserContent: () => Promise<BrowserContextCommandResult | undefined>;
	getContentBody: (
		type: ContentType,
		id: string | number,
	) => Promise<ReadContentCommandResult | undefined>;
	getStagingSpace: (id: string) => Promise<ReadSpaceCommandResult | undefined>;
	useCurrentNonEditingPageAsContext?: boolean;
	requestJiraContext?: () => Promise<JiraContextCommandResult | undefined>;
}) => {
	const actions = useChatContextStoreActions();
	const { getTarget, getCurrentTargetId } = actions;

	const fetchContent = useCallback(
		async (type: ContentType, contentContext: ContentContext, activeTargetId?: string | number) => {
			const currentTargetId = getCurrentTargetId();
			// Only use activeTargetId when aifc_multiple_context is enabled
			const targetId = fg('aifc_multiple_context') ? activeTargetId : undefined;
			const contentTarget = getTarget(contentContext, targetId);

			if (!contentTarget) {
				return undefined;
			}

			// Only use currentTargetId when staging-area doesn't exist
			// to avoid changing any content open behind the staging
			// area modal
			const shouldUseCurrentTargetId = type === 'page' && !getTarget('staging-area');

			const selectedTargetId = shouldUseCurrentTargetId
				? currentTargetId || contentTarget.id
				: contentTarget.id;

			const contentBody: ReadContentCommandResult | undefined = await getContentBody(
				type,
				selectedTargetId,
			);
			if (!contentBody || contentBody.contentType !== type) {
				return undefined;
			}

			switch (contentBody.contentType) {
				case 'page': {
					if (!contentBody.pageBody) {
						return undefined;
					}

					return {
						contentId: contentBody.contentId,
						document: {
							type: 'text/adf' as const,
							content: contentBody.pageBody,
						},
						selection: {
							type: 'text/markdown' as const,
							content: contentBody.selectionMarkdown || '',
						},
						selectionFragment: contentBody.selectionFragment,
						selectionLocalIds: contentBody.selectionLocalIds,
						isViewMode: contentBody.isViewMode,
						useGenericEditorSkill: contentBody.useGenericEditorSkill,
						additionalContext: contentBody.additionalContext,
					};
				}
				case 'whiteboard': {
					if (!contentBody.svg || !contentBody.wdf) {
						return undefined;
					}
					return {
						contentId: contentBody.contentId,
						type: 'image/svg+xml' as const,
						svg: contentBody.svg,
						title: contentBody.title ?? null,
						url: contentBody.url ?? null,
						wdf: contentBody.wdf,
						selectedElementIds: contentBody.selectedElementIds,
						isViewMode: contentBody.isViewMode,
					};
				}
				case 'database': {
					if (!contentBody.csv) {
						return undefined;
					}

					return {
						contentId: contentBody.contentId,
						csv: contentBody.csv,
						title: contentBody.title,
						url: contentBody.url,
					};
				}
				default:
					return undefined;
			}
		},
		[getContentBody, getTarget, getCurrentTargetId],
	);

	/**
	 * Fetches the context of a currently live page with Rovo chat open.
	 *
	 * @description see full list of cases to consider in {@link getContextRequirements}
	 */
	const getEditorContext = useCallback(
		async ({
			source,
			contentContext,
			isFirstPrompt,
			addCurrentPageAsContextUserPreference,
			activeTargetId,
		}: {
			source: string | undefined;
			contentContext: ContentContext;
			isFirstPrompt: boolean;
			addCurrentPageAsContextUserPreference: boolean;
			activeTargetId?: string | number;
		}) => {
			const isAifcEnabled = fg('aifc_create_enabled');
			const shouldProvideContext = getContextRequirements({
				contentContext,
				source,
				isFirstPrompt,
				isAifcEnabled,
				userPreference: addCurrentPageAsContextUserPreference,
			});
			if (!shouldProvideContext) {
				return undefined;
			}

			return isAifcEnabled
				? ((await fetchContent('page', contentContext, activeTargetId)) as EditorContextPayloadData)
				: actions.getEditorContext();
		},
		[actions, fetchContent],
	);

	/**
	 * Fetches the context of a currently live page with Rovo chat open.
	 *
	 * @description see full list of cases to consider in {@link getContextRequirements}
	 */
	const getWhiteboardContext = useCallback(
		async ({
			source,
			contentContext,
			isFirstPrompt,
			addCurrentPageAsContextUserPreference,
			activeTargetId,
		}: {
			source: string | undefined;
			contentContext: ContentContext;
			isFirstPrompt: boolean;
			addCurrentPageAsContextUserPreference: boolean;
			activeTargetId?: string | number;
		}) => {
			const isAifcEnabled = fg('aifc_create_enabled');
			const shouldProvideContext = getContextRequirements({
				contentContext,
				source,
				isFirstPrompt,
				isAifcEnabled,
				userPreference: addCurrentPageAsContextUserPreference,
			});
			if (!shouldProvideContext) {
				return undefined;
			}

			return isAifcEnabled
				? ((await fetchContent(
						'whiteboard',
						contentContext,
						activeTargetId,
					)) as WhiteboardContextPayloadData)
				: actions.getWhiteboardContext();
		},
		[actions, fetchContent],
	);

	const getDatabaseContext = useCallback(
		async ({
			source,
			contentContext,
			isFirstPrompt,
			addCurrentPageAsContextUserPreference,
			activeTargetId,
		}: {
			source: string | undefined;
			contentContext: ContentContext;
			isFirstPrompt: boolean;
			addCurrentPageAsContextUserPreference: boolean;
			activeTargetId?: string | number;
		}) => {
			const isAifcEnabled = fg('aifc_create_enabled');
			const shouldProvideContext = getContextRequirements({
				contentContext,
				source,
				isFirstPrompt,
				isAifcEnabled,
				userPreference: addCurrentPageAsContextUserPreference,
			});
			if (!shouldProvideContext) {
				return undefined;
			}

			return isAifcEnabled
				? ((await fetchContent(
						'database',
						contentContext,
						activeTargetId,
					)) as DatabaseContextPayloadData)
				: actions.getDatabaseContext();
		},
		[actions, fetchContent],
	);

	const getSpaceCreateContext = useCallback(
		({
			contentContext,
			isFirstPrompt,
		}: {
			contentContext: ContentContext;
			isFirstPrompt: boolean;
		}) => {
			if (
				contentContext === 'staging-area' &&
				!isFirstPrompt &&
				fg('aifc_space_creation_enabled')
			) {
				return getStagingSpace(String(getTarget(contentContext)?.id));
			}
		},
		[getStagingSpace, getTarget],
	);

	const getIsStagingArea = useCallback(
		(): boolean => actions.getActiveContext() === 'staging-area',
		[actions],
	);

	const useCurrentBrowsingContext = useCallback(
		(addCurrentPageAsContextUserPreference: boolean) => {
			// undefined means don't send useCurrentBrowsingContext at all. false tells backend to explicitly not use the browserUrl as context.
			if (useCurrentNonEditingPageAsContext === undefined) {
				return undefined;
			}
			const stagingAreaEnabled = getIsStagingArea();

			return (
				!stagingAreaEnabled &&
				addCurrentPageAsContextUserPreference &&
				useCurrentNonEditingPageAsContext
			);
		},
		[useCurrentNonEditingPageAsContext, getIsStagingArea],
	);

	const getBrowserContext = useCallback(
		async ({
			addCurrentPageAsContextUserPreference,
			enabled,
		}: {
			addCurrentPageAsContextUserPreference: boolean;
			enabled: boolean;
		}): Promise<BrowserContextPayloadData | undefined> => {
			// if the feature is not enabled, we return the original context resolution.
			if (
				!enabled &&
				!FeatureGates.getExperimentValue('jira_free_gen_editor', 'isEnabled', false)
			) {
				return actions.getBrowserContext();
			}

			if (!addCurrentPageAsContextUserPreference) {
				return undefined;
			}

			// Request browser content from the extension
			const browserContextResult = await requestBrowserContent();
			const browserContext = browserContextResult
				? { context: browserContextResult }
				: actions.getBrowserContext();

			return browserContext;
		},
		[requestBrowserContent, actions],
	);

	const getJiraContext = useCallback(async (): Promise<any> => {
		if (fg('jira-aifc-enable-preview-card-in-chat-v2')) {
			const jiraCreateContext = actions.getJiraCreateContext();
			if (jiraCreateContext) {
				return jiraCreateContext;
			}
		}

		// TODO: Remove requestJiraContext after cleaning up 'jira-aifc-enable-preview-card-in-chat-v2'
		// Fallback to bridge API if available (for backward compatibility)
		if (!requestJiraContext) {
			return undefined;
		}

		try {
			const result = await requestJiraContext();
			return result?.success ? result.context : undefined;
			// eslint-disable-next-line no-unused-vars
		} catch (error) {
			return undefined;
		}
	}, [requestJiraContext, actions]);

	return {
		...actions,
		getEditorContext,
		getWhiteboardContext,
		getSpaceCreateContext,
		getIsStagingArea,
		useCurrentBrowsingContext,
		getDatabaseContext,
		getBrowserContext,
		getJiraContext,
	};
};

type ContextRequirementOpts = {
	/**
	 * Current rendering context of Rovo chat
	 */
	contentContext: ContentContext;
	/**
	 * The indicated source of the interaction
	 */
	source: string | undefined;
	/**
	 * Whether this is the first prompt / message in a new conversation
	 */
	isFirstPrompt: boolean;
	/**
	 * Whether the user has toggled ON that they wish to use the context
	 * of the current content they are on
	 * @description NOTE: This is not respected verbatim. See {@link getContextRequirements}.
	 */
	userPreference: boolean;
	/**
	 * Whether AI first creation is enabled.
	 */
	isAifcEnabled: boolean;
};

/**
 * Determines if the context of a currently live piece of content should be yielded.
 *
 * @description
 * There are three cases to consider here:
 *
 * 1. Staging area context
 * 2. Inline editor
 * 3. Global context
 *
 * Our general heuristic is: we need to respect the user's preference if we are on real content, unless it
 * is an inline editing interaction. In the staging area, we similarly give inline editing experiences the
 * context at all times, but otherwise do NOT want to if it is a fresh conversation.
 *
 * Context is important for updates. Key aspect to consider: When does a user want to update their content?
 * When an inline edit is happening, they have explicitly selected content already, and hence want an edit.
 * If they are in the staging area and it is not the first prompt, they want an edit. Hence the below.
 */
export function getContextRequirements({
	contentContext,
	source,
	isFirstPrompt,
	isAifcEnabled,
	userPreference,
}: ContextRequirementOpts): boolean {
	/**
	 * If AIFC is not enabled, we default to the user's preference.
	 */
	if (!isAifcEnabled) {
		return userPreference;
	}

	/**
	 * In the staging area, we always want to provide context if the source is the
	 * inline editor, or if the prompt is not the first in a new conversation.
	 */

	const sourceEligible =
		source === 'CONFLUENCE_INLINE_EDITOR' ||
		source === 'INLINE_EDITOR' ||
		source === 'CONFLUENCE_WHITEBOARD_TO_PAGE_BUTTON' ||
		source === 'CONFLUENCE_WHITEBOARD_BRAINSTORMING_FOLLOW_UP';
	if (contentContext === 'staging-area') {
		return sourceEligible || !isFirstPrompt;
	} else {
		/**
		 * On real content when AIFC is enabled, we only want to default to true
		 * if the source of the content is the inline editing experience. Otherwise,
		 * we want to respect the preference chosen by the user.
		 */
		return sourceEligible || userPreference;
	}
}
